import moment from 'moment'
import { get, isEqual } from 'lodash'
import { VEHICLE_INSPECTION_STATUS_BY_TRAFI_CODE } from 'lib/utils/store/vehicle'
import { logException } from 'lib/errors'
import { debounceValidateRegistrationNumber } from 'lib/utils/formValidators'

export default class Vehicle {
    vehicle
    keyValues

    constructor (vehicle) {
        this.vehicle = vehicle
        this.keyValues = vehicle.keyvalues ? JSON.parse(vehicle.keyvalues) : {}
    }

    static create (vehicle) {
        return new Vehicle(vehicle)
    }

    getId () {
        return this.vehicle.id
    }

    getName () {
        return this.vehicle.name
    }

    getKeyValue (key) {
        return this.keyValues[key]
    }

    getImages () {
        return this.vehicle.images
    }

    getProfileImage () {
        return this.vehicle.profileImage
    }

    getRegNumber () {
        return this.vehicle.regNumber
    }

    getIsFinnishRegNumber () {
        return this.vehicle.isFinnishRegNumber !== null ? this.vehicle.isFinnishRegNumber : true
    }

    getVertical () {
        return this.vehicle.vertical
    }

    getVehicleTypeId () {
        return this.vehicle.vehicleType && this.vehicle.vehicleType.id
    }

    getVehicleTypeName () {
        return this.vehicle.vehicleType?.vehicleType
    }

    getTrafiData (format) {
        return this.vehicle.trafiData
    }

    getPrefilledAt () {
        return this.vehicle.prefilledAt
    }

    getLastTrafiDataFetchDate (format) {
        const trafiData = this.vehicle.trafiData
        const value = trafiData ? trafiData.updatedAt || trafiData.createdAt : null
        if (format) return moment(value).format(format)
        return value
    }

    getRelatedAdId () {
        return this.vehicle.relatedAd
    }

    getVehicleRegistrationDate () {
        return this.getLastTrafiDataFetchDate()
    }

    getVehicleRegistrationSummary (t) {
        const value = this.getVehicleRegistrationDate()
        if (value) return t('vehicleProfile:fetched', { date: moment(value).format('DD.MM.YYYY') })
        return '-'
    }

    getVehicleInspectionValidUntil (format) {
        if (!this.vehicle.trafiData) return null

        const value = this.vehicle.trafiData.vehicleDateOfInspectionValidUntil
        if (format) return value ? moment(value).format(format) : '-'
        return value
    }

    getVehicleLastInspection (format) {
        if (!this.vehicle.trafiData) return null

        const value = this.vehicle.trafiData.vehicleDateOfLastInspection
        if (format) return value ? moment(value).format(format) : '-'
        return value
    }

    getVehicleLastInspectionStatusTrafiCode () {
        return get(this.vehicle, 'trafiData.vehicleInspectionStatus', null)
    }

    getVehicleLastInspectionStatus () {
        const code = this.getVehicleLastInspectionStatusTrafiCode()
        return code ? VEHICLE_INSPECTION_STATUS_BY_TRAFI_CODE[code] : null
    }

    getVehicleLastInspectionStatusName (t) {
        return this.vehicle.trafiData.vehicleInspectionStatus
            ? t(`trafi:inspectionStatus.${this.vehicle.trafiData.vehicleInspectionStatus}`)
            : '-'
    }

    vehicleInspectionIsValid () {
        const validUntil = get(this.vehicle, 'trafiData.vehicleDateOfInspectionValidUntil', false)
        return validUntil && moment(validUntil).isAfter(Date.now())
    }

    getVehicleInspectionStatus (t) {
        const dateFormatted = this.getVehicleInspectionValidUntil('DD.MM.YYYY')

        if (this.vehicleInspectionIsValid()) {
            return {
                valid: true,
                dateFormatted,
                content: t('vehicleProfile:sections.inspectionInformation.valid', { date: dateFormatted })
            }
        }

        return {
            valid: false,
            dateFormatted,
            content: t('vehicleProfile:sections.inspectionInformation.invalid', { date: dateFormatted })
        }
    }

    fields = {}

    getFieldsForFieldSchema (t, fieldNames) {
        return fieldNames.map((fieldName) => {
            const fieldSchemaFnc = this.fields[fieldName]
            if (!fieldSchemaFnc) {
                console.log('Vehicle: ', this.vehicle)
                logException(`Invalid field name "${fieldName}"`)
            }
            return {
                name: fieldName,
                ...fieldSchemaFnc(t, fieldName),
            }
        })
    }

    static getFieldValidations (fields) {
        return fields.reduce((result, field) => {
            result[field.name] = field.validation
            return result
        }, {})
    }

    static getFieldInitialValues (fields) {
        return fields.reduce((result, field) => {
            result[field.name] = field.initialValue || ''

            if (field.type && field.type === 'boolean') {
                result[field.name] = field.initialValue || false
            }

            return result
        }, {})
    }

    async validateRegNumber (value, formValues) {
        if ((!value || !formValues.isFinnishRegNumber) || this.getRegNumber() === value) return undefined
        const vertical = this.getVertical()
        const validity = await debounceValidateRegistrationNumber(value, vertical)
        return validity
    }

    getPrimaryDetailsValidationSchema (fields) {
        throw new Error('Not implemented')
    }

    getPrimaryDetailsInitialValues (fields) {
        throw new Error('Not implemented')
    }

    getPrimaryDetailsFieldsLayoutSchema () {
        throw new Error('Not implemented')
    }

    getPrimaryDetailsFieldsSchema (t) {
        throw new Error('Not implemented')
    }

    showTrafiDataToUser (trafiData) {
        if (!trafiData) return false

        if (trafiData.regNumber && (trafiData.regNumber !== this.vehicle.regNumber)) return false

        const ownerType = get(this, 'vehicle.formOfOwnership.ownerType', null)
        return ownerType === 'owner' || ownerType === 'holder'
    }

    /** New pending data **/

    getVehicleAddTimelineEventNotificationPending () {
        return this.getKeyValue('vehicleAddTimelineEventNotificationStatus') === 'PENDING'
    }

    getHasNewDetails () {
        return this.getKeyValue('vehicleAddPrefillNotificationStatus') === 'PENDING'
    }

    /** Utils **/

    isEqual (wrappedVehicle) {
        return !wrappedVehicle || isEqual(this.vehicle, wrappedVehicle.vehicle)
    }
}
